// Shared "has this event already passed?" rule — a STYLING signal only.
//
// History (read this before "restoring" a count helper here): ticket #305 made a
// contact's Events section LIST past events but deliberately EXCLUDE them from
// its "Total: N", on the reasoning that a finished meeting is not a live
// commitment. The PO reversed that in "Total Events shared": the Total must count
// EVERY logged event, so the number always equals the number of rows rendered
// beneath it. A total that disagrees with a visible list reads as a bug, whatever
// the reasoning behind it.
//
// So counting no longer lives here at all. The Events drawer totals the very
// array it maps over (see GroupedDrawer in components/contacts/InteractionBar),
// which makes "Total == rows rendered" structural rather than a second
// computation that can drift from the first.
//
// What survives is the past/not-past boundary, still used to DE-EMPHASIZE a
// finished event and to stamp `data-past` on its row. The boundary is the event's
// EFFECTIVE END, not its start — an event running right now has not passed. When
// an event carries no `end_time` we fall back to start + 1h, the duration
// convention already used across this codebase (`calendar_sharing.find_conflicts`
// treats a NULL end as 1h, and the Calendar's Upcoming Events cleanup drops a
// no-end event at start + 1h). Reusing it keeps the contact timeline and the
// calendar from disagreeing about the same event.
//
// Timestamps are absolute instants (the API serializes UTC with a `Z`), so the
// comparison is timezone-independent — a viewer's timezone changes how the event
// is DISPLAYED, never whether it has already happened.

export const NO_END_DURATION_MS = 60 * 60 * 1000;

/** The instant an event stops being current: `end_time`, else start + 1h. */
export const effectiveEndMs = (event) => {
    const end = Date.parse(event?.end_time || '');
    if (!Number.isNaN(end)) return end;
    const start = Date.parse(event?.start_time || '');
    if (!Number.isNaN(start)) return start + NO_END_DURATION_MS;
    return NaN;
};

/** Has this event already finished? An undated / unparseable row is never past —
 *  there is no evidence it happened, and it already sorts into the trailing "—"
 *  group, so greying it out on top of that would hide it twice over. */
export const isPastEvent = (event, now = Date.now()) => {
    const end = effectiveEndMs(event);
    if (Number.isNaN(end)) return false;
    return end < now;
};
